import { useEffect, useRef, useState } from 'react';

// Sparkline — a dependency-free SVG line chart for a small time series. Only
// present (non-null) points are plotted and connected; a series with fewer than
// two present points renders nothing. Auto-scales to the series' own min/max so
// small movements stay visible. The x-axis follows REAL time, not run index —
// a burst of recent points or a quiet stretch reads truthfully. Points must be
// ordered oldest → newest.
//
// Shared primitive (lifted from Pulse.tsx). The pulse trend strip and the home
// dashboard's eNPS tile both render through this one implementation.
//
// `fluid` swaps the fixed pixel width for "whatever the flex parent leaves us",
// measured with a ResizeObserver. It is measured rather than done in CSS
// (viewBox + preserveAspectRatio="none") because scaling the viewBox stretches
// the geometry: the latest-point dot would render as an ellipse and the
// time-axis spacing would distort with the container. Measuring keeps every
// x-position in real pixels, so a wide row genuinely shows more resolution
// instead of a zoomed-in small chart.
export function Sparkline({
  points,
  width = 132,
  height = 30,
  color = 'var(--ink)',
  fluid = false,
}: {
  points: { at: number; v: number | null }[];
  width?: number;
  height?: number;
  color?: string;
  fluid?: boolean;
}) {
  // Hooks run before any early return — `points` can be too short to plot, and
  // bailing out above a hook would break the call order.
  const hostRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<number | null>(null);
  useEffect(() => {
    if (!fluid) return;
    const el = hostRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect.width ?? 0);
      // Ignore a 0 measurement (hidden tab / unmounting) so the chart keeps its
      // last good width instead of collapsing to a dot.
      if (w > 0) setMeasured(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fluid]);
  const present = points
    .map((p, i) => ({ at: p.at, v: p.v, i }))
    .filter((p): p is { at: number; v: number; i: number } => p.v != null);
  if (present.length < 2) return null;
  // Before the first measurement the fixed default keeps the first paint sane;
  // the observer corrects it on the next frame.
  const w = fluid ? (measured ?? width) : width;
  const vs = present.map((p) => p.v);
  const min = Math.min(...vs);
  const max = Math.max(...vs);
  const span = max - min || 1;
  const n = points.length;
  const t0 = points[0]!.at;
  const tSpan = points[n - 1]!.at - t0;
  // Position by timestamp; fall back to even index spacing only if every point
  // shares one instant (tSpan === 0), which would otherwise divide by zero.
  const px = (at: number, i: number) =>
    tSpan > 0
      ? ((at - t0) / tSpan) * (w - 4) + 2
      : n <= 1
        ? 2
        : (i / (n - 1)) * (w - 4) + 2;
  const py = (v: number) => height - 3 - ((v - min) / span) * (height - 6);
  const d = present
    .map((p, k) => `${k === 0 ? 'M' : 'L'}${px(p.at, p.i).toFixed(1)},${py(p.v).toFixed(1)}`)
    .join(' ');
  const last = present[present.length - 1]!;
  const chart = (
    <svg width={w} height={height} className={fluid ? undefined : 'shrink-0'} aria-hidden>
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={px(last.at, last.i)} cy={py(last.v)} r={2.5} fill={color} />
    </svg>
  );
  if (!fluid) return chart;
  // A 7rem floor rather than min-w-0: with flex-basis 0 a growable item shrinks
  // indefinitely and never triggers the parent's flex-wrap, so on a narrow
  // screen the chart would collapse to a sliver while the siblings stayed on
  // one line. The floor makes the siblings wrap instead, and the chart stays
  // legible at 320px.
  return (
    <div ref={hostRef} className="min-w-[7rem] flex-1">
      {chart}
    </div>
  );
}
